/**
 * Hook para gerenciar sessão do visitante
 * Persiste identificação no localStorage e gera UUID anônimo
 * 
 * IMPORTANTE: Quando um forcedVisitorId é fornecido (modo autenticado/interno),
 * ele tem prioridade absoluta sobre o UUID anônimo do localStorage.
 * Isso garante que o visitor_id seja consistente com o user.id do usuário logado,
 * permitindo que o histórico persista após refresh (CTRL+R).
 */

import { useState, useEffect, useCallback } from 'react';
import type { VisitorData } from '../types';

// Chave do localStorage para dados do visitante
const STORAGE_KEY = 'uaizychat_visitor';

/**
 * Gera UUID v4 simples para visitantes anônimos (widget público)
 */
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Hook para gerenciar sessão do visitante
 * 
 * @param inboxId - ID da inbox para isolamento de dados por inbox
 * @param forcedVisitorId - ID forçado para modo autenticado (user.id do Supabase Auth)
 *                          Quando fornecido, substitui o UUID anônimo garantindo
 *                          consistência entre envio, realtime e histórico.
 */
export const useVisitorSession = (inboxId: string, forcedVisitorId?: string) => {
  const [visitor, setVisitor] = useState<VisitorData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carrega ou cria sessão do visitante, respeitando forcedVisitorId quando presente
  useEffect(() => {
    const loadSession = () => {
      try {
        const storageKey = `${STORAGE_KEY}_${inboxId}`;
        const stored = localStorage.getItem(storageKey);
        
        // Se há um forcedVisitorId (modo autenticado), usa ele como visitor_id
        // Isso é essencial para o chat interno funcionar com validação JWT do backend
        if (forcedVisitorId) {
          const forcedVisitor: VisitorData = {
            visitor_id: forcedVisitorId,
            // Preserva nome/email/phone do storage se existir
            ...(stored ? JSON.parse(stored) : {}),
          };
          // Sobrescreve visitor_id para garantir que seja sempre o forcedVisitorId
          forcedVisitor.visitor_id = forcedVisitorId;
          localStorage.setItem(storageKey, JSON.stringify(forcedVisitor));
          setVisitor(forcedVisitor);
        } else if (stored) {
          // Modo anônimo: usa dados do localStorage
          const parsed = JSON.parse(stored) as VisitorData;
          setVisitor(parsed);
        } else {
          // Modo anônimo: cria novo visitante com UUID aleatório
          const newVisitor: VisitorData = {
            visitor_id: generateUUID(),
          };
          localStorage.setItem(storageKey, JSON.stringify(newVisitor));
          setVisitor(newVisitor);
        }
      } catch (error) {
        console.error('[UaizyChat] Erro ao carregar sessão:', error);
        // Fallback: cria visitante em memória
        setVisitor({ visitor_id: generateUUID() });
      } finally {
        setIsLoading(false);
      }
    };

    // Aguarda inboxId para carregar sessão
    if (inboxId) {
      loadSession();
    }
  }, [inboxId, forcedVisitorId]);

  // Atualiza dados do visitante (nome, email, phone)
  const updateVisitor = useCallback((data: Partial<VisitorData>) => {
    setVisitor((prev) => {
      if (!prev) return prev;
      
      const updated = { ...prev, ...data };
      
      try {
        const storageKey = `${STORAGE_KEY}_${inboxId}`;
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (error) {
        console.error('[UaizyChat] Erro ao salvar sessão:', error);
      }
      
      return updated;
    });
  }, [inboxId]);

  /**
   * Identifica visitante com dados de contato
   * Preserva o visitor_id atual (seja forçado ou anônimo)
   */
  const identify = useCallback((name?: string, email?: string, phone?: string) => {
    updateVisitor({ name, email, phone });
  }, [updateVisitor]);

  /**
   * Limpa sessão do visitante
   * No modo autenticado (forcedVisitorId), mantém o mesmo ID mas limpa dados de perfil
   * No modo anônimo, gera um novo UUID
   */
  const clearSession = useCallback(() => {
    try {
      const storageKey = `${STORAGE_KEY}_${inboxId}`;
      
      // No modo autenticado, mantém o forcedVisitorId para consistência
      if (forcedVisitorId) {
        const clearedVisitor: VisitorData = { visitor_id: forcedVisitorId };
        localStorage.setItem(storageKey, JSON.stringify(clearedVisitor));
        setVisitor(clearedVisitor);
      } else {
        // No modo anônimo, remove e gera novo UUID
        localStorage.removeItem(storageKey);
        setVisitor({ visitor_id: generateUUID() });
      }
    } catch (error) {
      console.error('[UaizyChat] Erro ao limpar sessão:', error);
    }
  }, [inboxId, forcedVisitorId]);

  return {
    visitor,
    isLoading,
    updateVisitor,
    identify,
    clearSession,
  };
};
